import React, { useState, useEffect } from 'react';
import { get, post } from '../services/apiService';
import { User, ArrowLeft, Mail, Calendar, Award, Download, Receipt, Package, X, Eye } from 'lucide-react';
import { Link } from 'react-router-dom';
import logger from '../utils/logger';
import jsPDF from 'jspdf';
import { getFullUrl } from '../config/apiConfig';
import {
  formatLocalDate,
  formatCalendarDate,
  formatCalendarExpiryDate,
  formatCalendarDateForFilename,
} from '../utils/dateUtils';

const UserDetails = () => {
  const [userDetails, setUserDetails] = useState(null);
  const [purchaseDetails, setPurchaseDetails] = useState(null);
  const [exams, setExams] = useState([]);
  const [completedExams, setCompletedExams] = useState([]);
  const [activeGrade, setActiveGrade] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [showAnswersModal, setShowAnswersModal] = useState(false);
  const [examAnswers, setExamAnswers] = useState(null);
  const [answersLoading, setAnswersLoading] = useState(false);
  const [showMyAnswersModal, setShowMyAnswersModal] = useState(false);
  const [selectedExamAnswers, setSelectedExamAnswers] = useState(null);

  useEffect(() => {
    fetchUserDetails();
    fetchPurchaseDetails();
    fetchExams();
  }, []);

  const fetchPurchaseDetails = async () => {
    try {
      // Get user info from localStorage (token is now in httpOnly cookie)
      const userInfo = localStorage.getItem('userInfo');
      if (!userInfo) {
        throw new Error('User not logged in');
      }

      const user = JSON.parse(userInfo);
      const userId = user.id;

      logger.log('🔍 Fetching purchase details for userId:', userId);

      const response = await get(getFullUrl('purchases'));
      const data = await response.json();


      // Check if data is directly the purchase data or if it's in data.body
      const purchaseData = data.body || data;

      setPurchaseDetails(purchaseData);
    } catch (err) {
      logger.error('❌ Purchase API Error:', err);
      // Don't set error state for purchase details, just log it
    }
  };

  const downloadReceiptPDF = (purchase) => {
    try {
      // Create PDF document
      const doc = new jsPDF();

      // Set up page dimensions
      const pageWidth = doc.internal.pageSize.getWidth();

      // Professional Receipt format
      doc.setFont('helvetica');

      // ===== HEADER =====
      doc.setFontSize(18);
      doc.text("PAYMENT", pageWidth - 20, 20, { align: "right" });
      doc.text("RECEIPT", pageWidth - 23, 26, { align: "right" });
      doc.text("_________", pageWidth - 20, 27, { align: "right" });

      doc.setFontSize(10);
      doc.text("QBYTE Technology pty ltd", 14, 18);
      doc.text("Trading as NextGen Exam platform", 14, 22);
      doc.text("ABN : 78697357510", 14, 28);
      doc.text("support@nepexams.com.au", 14, 32);

      // ===== BILL TO =====
      doc.setFontSize(11);
      doc.text("Bill To:", 14, 40);
      doc.text(purchase.name, 14, 46);
      doc.text(purchase.email, 14, 52);

      // ===== INVOICE DETAILS =====
      doc.text(`Invoice #: ${purchase.invoiceId}`, pageWidth - 14, 40, { align: "right" });
      doc.text(`Date: ${formatCalendarDate(purchase.purchaseDate)}`, pageWidth - 14, 46, { align: "right" });
      doc.text(`Payment ID: ${purchase.paymentId}`, pageWidth - 14, 52, { align: "right" });

      // ===== TABLE HEADER =====
      let y = 70;
      doc.line(14, y, pageWidth - 14, y);

      y += 6;
      doc.text("Description", 14, y);
      doc.text("Qty", 100, y);
      doc.text("Price", 130, y);
      doc.text("Total", pageWidth - 14, y, { align: "right" });

      y += 4;
      doc.line(14, y, pageWidth - 14, y);

      // ===== ITEM =====
      y += 8;
      const packageName = purchase.packageId.replace('_', ' ');
      const packageDescription = `Grade ${purchase.grade || 'N/A'} - ${packageName} Package`;
      doc.text(packageDescription, 14, y);
      doc.text("1", 100, y);
      doc.text(`$${purchase.amount}`, 130, y);
      doc.text(`$${purchase.amount}`, pageWidth - 14, y, { align: "right" });

      y += 4;
      doc.line(14, y, pageWidth - 14, y);

      // ===== SUMMARY =====
      y += 10;

      const gst = (purchase.amount * 0.1).toFixed(2);
      const total = purchase.amount;

      doc.text(`Subtotal: $${purchase.amount}`, pageWidth - 14, y, { align: "right" });
      y += 6;
      doc.text(`GST (10%) Included`, pageWidth - 14, y, { align: "right" });
      y += 6;

      doc.setFont("helvetica", "bold");
      doc.text(`Total: $${total}`, pageWidth - 14, y, { align: "right" });

      // ===== FOOTER =====
      y += 20;
      doc.setFont("helvetica", "normal");
      doc.text("Thank you for your purchase!", pageWidth / 2, y, { align: "center" });
      doc.text("Website: www.nepexams.com.au", pageWidth / 2, y + 4, { align: "center" });

      // Save the PDF
      const pdfBlob = doc.output('blob');
      const url = window.URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `invoice_${formatCalendarDateForFilename(purchase.purchaseDate)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      logger.log('📄 Professional PDF invoice downloaded for purchase:', purchase.checkoutSessionId);
    } catch (error) {
      logger.error('❌ Error downloading PDF invoice:', error);
    } finally {
      setDownloadLoading(false);
    }
  };

  const fetchUserDetails = async () => {
    setLoading(true);
    setError(null);

    try {
      const userInfo = localStorage.getItem('userInfo');
      if (!userInfo) {
        throw new Error('User not logged in');
      }

      const user = JSON.parse(userInfo);
      const userId = user.id;

      if (!userId) {
        throw new Error('User ID not found in token');
      }

      logger.log('🔍 Extracted userId:', userId);

      const response = await get(getFullUrl(`user/${userId}`));
      const data = await response.json();


      logger.log('✅ API Response Status:', response.status);
      logger.log('✅ User Details:', data);
      logger.log('✅ Response Headers:', Object.fromEntries(response.headers.entries()));

      // Parse the actual user data from data.body
      const userData = typeof data.body === 'string' ? JSON.parse(data) : data;
      logger.log('✅ Parsed User Data:', userData);

      // If API returns valid data, use it
      if (userData && userData.name) {
        setUserDetails(userData);
      } else {
        // Fallback to localStorage data if API fails or returns null
        logger.log('⚠️ API returned invalid data, using localStorage fallback');
        setUserDetails({
          name: user.name,
          email: user.email,
          mobileNumber: user.mobileNumber || 'N/A',
          status: 'active',
          registrationDate: user.memberSince || new Date().toISOString()
        });
      }
    } catch (err) {
      logger.error('❌ API Error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchExams = async () => {
    try {
      const response = await get(getFullUrl('exams'));

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Parse the response body
      let actualData = data;
      if (data && data.body) {
        actualData = typeof data.body === 'string' ? JSON.parse(data.body) : data.body;
      }

      // Handle the backend structure where examSets is the main array
      const examsArray = [];

      if (actualData && actualData.examSets && Array.isArray(actualData.examSets)) {
        actualData.examSets.forEach(set => {
          examsArray.push({
            id: set.examId,
            title: set.type || 'Exam',
            grade: set.PK ? set.PK.split('#')[1] : 'Unknown'
          });
        });
      }

      setExams(examsArray);

      // Extract completed exams from the API response
      if (actualData && actualData.completedExams && Array.isArray(actualData.completedExams)) {
        setCompletedExams(actualData.completedExams);
        // Set default active grade to first available grade
        if (actualData.completedExams.length > 0) {
          const grades = [...new Set(actualData.completedExams.map(e => e.grade).filter(Boolean))].sort((a, b) => a - b);
          if (grades.length > 0 && !activeGrade) {
            setActiveGrade(grades[0]);
          }
        }
        logger.log('✅ Completed Exams:', actualData.completedExams);
      } else {
        setCompletedExams([]);
      }
    } catch (err) {
      logger.error('Error fetching exams:', err);
    }
  };

  const fetchExamAnswers = async (examId) => {
    setAnswersLoading(true);
    try {
      const response = await post(getFullUrl('exam-with-answers'), {
        examId: examId
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Parse the response body
      let actualData = data;
      if (data && data.body) {
        actualData = typeof data.body === 'string' ? JSON.parse(data.body) : data.body;
      }

      // Handle the nested structure where questions are inside data.questions
      if (actualData && actualData.data && actualData.data.questions) {
        setExamAnswers(actualData.data);
      } else {
        setExamAnswers(actualData);
      }
      setShowAnswersModal(true);
      logger.log('✅ Exam Answers:', actualData);
    } catch (err) {
      logger.error('Error fetching exam answers:', err);
      alert('Failed to load exam answers. Please try again.');
    } finally {
      setAnswersLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-8">
          <div className="text-center">
            <User className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Error Loading User Details</h2>
            <p className="text-gray-600">{error}</p>
            <button
              onClick={fetchUserDetails}
              className="mt-4 bg-primary-600 text-white px-4 py-2 rounded hover:bg-primary-700"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <Link
            to="/dashboard"
            className="inline-flex items-center text-primary-600 hover:text-primary-500 mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Dashboard
          </Link>
          <h1 className="text-3xl font-bold text-gray-900">User Details</h1>
        </div>

        {/* User Info Card */}
        <div className="bg-white shadow-lg rounded-lg overflow-hidden">
          <div className="bg-primary-600 px-6 py-4">
            <h2 className="text-xl font-semibold text-white">Profile Information</h2>
          </div>

          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Basic Info */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h3>

                <div className="space-y-3">
                  <div className="flex items-center">
                    <User className="h-5 w-5 text-gray-400 mr-3" />
                    <div>
                      <p className="text-sm text-gray-500">Name</p>
                      <p className="font-medium text-gray-900">{userDetails?.name || 'N/A'}</p>
                    </div>
                  </div>

                  <div className="flex items-center">
                    <Mail className="h-5 w-5 text-gray-400 mr-3" />
                    <div>
                      <p className="text-sm text-gray-500">Email</p>
                      <p className="font-medium text-gray-900">{userDetails?.email || 'N/A'}</p>
                    </div>
                  </div>

                  <div className="flex items-center">
                    <svg className="h-5 w-5 text-gray-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                    <div>
                      <p className="text-sm text-gray-500">Mobile</p>
                      <p className="font-medium text-gray-900">{userDetails?.mobileNumber || 'N/A'}</p>
                    </div>
                  </div>

                  <div className="flex items-center">
                    <svg className="h-5 w-5 text-gray-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div>
                      <p className="text-sm text-gray-500">Status</p>
                      <p className="font-medium text-gray-900">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${userDetails?.status === 'active'
                            ? 'bg-green-100 text-green-800'
                            : userDetails?.status === 'inactive'
                              ? 'bg-red-100 text-red-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                          {userDetails?.status || 'N/A'}
                        </span>
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center">
                    <Calendar className="h-5 w-5 text-gray-400 mr-3" />
                    <div>
                      <p className="text-sm text-gray-500">Member Since</p>
                      <p className="font-medium text-gray-900">
                        {userDetails?.memberSince
                          ? formatLocalDate(userDetails.memberSince)
                          : userDetails?.createdAt
                            ? formatLocalDate(userDetails.createdAt)
                            : 'N/A'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Stats */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Statistics</h3>

                <div className="space-y-3">
                  <div className="flex items-center">
                    <Award className="h-5 w-5 text-gray-400 mr-3" />
                    <div>
                      <p className="text-sm text-gray-500">Total Exams</p>
                      <p className="font-medium text-gray-900">{exams.length}</p>
                    </div>
                  </div>

                  <div className="flex items-center">
                    <Award className="h-5 w-5 text-gray-400 mr-3" />
                    <div>
                      <p className="text-sm text-gray-500">Average Score</p>
                      <p className="font-medium text-gray-900">
                        {completedExams.length > 0
                          ? Math.round(completedExams.reduce((acc, exam) => acc + (exam.percentage || 0), 0) / completedExams.length)
                          : 0}%
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center">
                    <Award className="h-5 w-5 text-gray-400 mr-3" />
                    <div>
                      <p className="text-sm text-gray-500">Best Score</p>
                      <p className="font-medium text-gray-900">
                        {completedExams.length > 0
                          ? Math.max(...completedExams.map(exam => exam.percentage || 0))
                          : 0}%
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>


        {/* Completed Exams History */}
        {completedExams.length > 0 && (
          <div className="mt-8 bg-white shadow-lg rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Exam History</h3>
              <Award className="h-5 w-5 text-gray-400" />
            </div>

            {/* Grade Tabs */}
            {(() => {
              const grades = [...new Set(completedExams.map(e => e.grade).filter(Boolean))].sort((a, b) => a - b);
              return grades.length > 1 ? (
                <div className="flex gap-2 mb-4 overflow-x-auto pb-2">
                  {grades.map(grade => {
                    const gradeExams = completedExams.filter(e => e.grade === grade);
                    const avgScore = gradeExams.length > 0
                      ? Math.round(gradeExams.reduce((acc, e) => acc + (e.percentage || 0), 0) / gradeExams.length)
                      : 0;
                    return (
                      <button
                        key={grade}
                        onClick={() => setActiveGrade(grade)}
                        className={`flex flex-col items-center px-4 py-2 rounded-lg border-2 transition-all min-w-[80px] ${activeGrade === grade
                            ? 'border-primary-600 bg-primary-50'
                            : 'border-gray-200 hover:border-gray-300'
                          }`}
                      >
                        <span className={`text-sm font-semibold ${activeGrade === grade ? 'text-primary-700' : 'text-gray-700'
                          }`}>
                          Grade {grade}
                        </span>
                        <span className="text-xs text-gray-500">
                          {gradeExams.length} exams · Avg {avgScore}%
                        </span>
                      </button>
                    );
                  })}
                </div>
              ) : null;
            })()}

            {/* Desktop Table Headers - Hidden on Mobile */}
            <div className="hidden sm:grid grid-cols-12 gap-2 px-4 py-2 bg-gray-100 rounded-t-lg text-sm font-medium text-gray-700">
              <div className="col-span-1">Grade</div>
              <div className="col-span-1">Package</div>
              <div className="col-span-1">Type</div>
              <div className="col-span-2 text-center">Set</div>
              <div className="col-span-1 text-center">Score</div>
              <div className="col-span-2 text-right">Percentage</div>
              <div className="col-span-4 text-center">Actions</div>
            </div>

            <div className="space-y-1 max-h-[400px] overflow-y-auto pr-2 border border-t-0 border-gray-200 rounded-b-lg">
              {completedExams.filter(exam => !activeGrade || exam.grade === activeGrade).map((exam, index) => (
                <div key={index}>
                  {/* Mobile Card Layout */}
                  <div className="sm:hidden bg-white border border-gray-200 rounded-lg p-4 mb-3 hover:shadow-md transition-shadow">
                    <div className="flex items-center justify-between mb-3">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${exam.percentage >= 80
                          ? 'bg-green-100 text-green-800'
                          : exam.percentage >= 60
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                        Grade {exam.grade}
                      </span>
                      <span className="text-lg font-bold text-gray-900">
                        {exam.percentage !== undefined ? `${exam.percentage}%` : 'N/A'}
                      </span>
                    </div>

                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-500">Type:</span>
                        <span className="font-medium text-gray-900">{exam.type}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Package:</span>
                        <span className="text-gray-600">{exam.package || '-'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Set:</span>
                        <span className="text-gray-700">
                          {exam.set && exam.set !== "0" ? exam.set : '-'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Score:</span>
                        <span className="font-medium text-gray-900">
                          {exam.score !== undefined ? exam.score : 'N/A'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Date:</span>
                        <span className="text-xs text-gray-400">
                          {exam.lastAttemptDate
                            ? formatLocalDate(exam.lastAttemptDate)
                            : 'N/A'}
                        </span>
                      </div>
                    </div>

                    <div className="mt-4 pt-3 border-t border-gray-200 space-y-2">
                      <button
                        onClick={() => fetchExamAnswers(exam.examId)}
                        disabled={answersLoading}
                        className="w-full flex items-center justify-center px-3 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                      >
                        <Eye className="h-4 w-4 mr-2" />
                        {answersLoading ? 'Loading...' : 'View Solution'}
                      </button>
                      <button
                        onClick={() => {
                          setSelectedExamAnswers(exam);
                          setShowMyAnswersModal(true);
                        }}
                        className="w-full flex items-center justify-center px-3 py-2 bg-green-600 text-white text-sm rounded hover:bg-green-700 transition-colors"
                      >
                        <Eye className="h-4 w-4 mr-2" />
                        My Answers
                      </button>
                    </div>
                  </div>

                  {/* Desktop Table Row */}
                  <div className="hidden sm:grid grid-cols-12 gap-2 px-4 py-3 border-b border-gray-100 last:border-b-0 hover:bg-gray-50 transition-colors items-center">
                    <div className="col-span-1">
                      <span className={`inline-flex px-1 py-1 text-xs font-semibold rounded-full ${exam.percentage >= 80
                          ? 'bg-green-100 text-green-800'
                          : exam.percentage >= 60
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                        G{exam.grade}
                      </span>
                    </div>
                    <div className="col-span-1">
                      <span className="text-sm text-gray-600 truncate">{exam.package || '-'}</span>
                    </div>
                    <div className="col-span-1">
                      <span className="text-sm text-gray-900 font-medium truncate">{exam.type}</span>
                    </div>
                    <div className="col-span-2 text-center">
                      {exam.set && exam.set !== "0" ? (
                        <span className="text-sm text-gray-700">{exam.set}</span>
                      ) : (
                        <span className="text-sm text-gray-400">-</span>
                      )}
                    </div>
                    <div className="col-span-1 text-center">
                      <span className="text-sm font-medium text-gray-900">
                        {exam.score !== undefined ? exam.score : 'N/A'}
                      </span>
                    </div>
                    <div className="col-span-2 text-right">
                      <div className="text-sm font-semibold text-gray-900">
                        {exam.percentage !== undefined ? `${exam.percentage}%` : 'N/A'}
                      </div>
                      <div className="text-xs text-gray-400">
                        {exam.lastAttemptDate
                          ? formatLocalDate(exam.lastAttemptDate)
                          : 'N/A'}
                      </div>
                    </div>
                    <div className="col-span-4 text-center flex items-center justify-center gap-1">
                      <button
                        onClick={() => fetchExamAnswers(exam.examId)}
                        disabled={answersLoading}
                        className="flex items-center justify-center px-2 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                      >
                        <Eye className="h-3 w-3 mr-1" />
                        {answersLoading ? '...' : 'View Solution'}
                      </button>
                      <button
                        onClick={() => {
                          setSelectedExamAnswers(exam);
                          setShowMyAnswersModal(true);
                        }}
                        className="flex items-center justify-center px-2 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700 transition-colors"
                      >
                        <Eye className="h-3 w-3 mr-1" />
                        My Answers
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {completedExams.length > 5 && (
              <div className="text-center mt-3 text-xs text-gray-500">
                Showing {completedExams.length} completed exams
              </div>
            )}
          </div>
        )}

        {/* Purchase History */}
        <div className="mt-8 bg-white shadow-lg rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">Purchase History</h3>
            <Package className="h-5 w-5 text-gray-400" />
          </div>

          {purchaseDetails?.purchases && purchaseDetails.purchases.length > 0 ? (
            <div className={`space-y-4 ${purchaseDetails.purchases.length > 3 ? 'max-h-[500px] overflow-y-auto pr-2' : ''}`}>
              {purchaseDetails.purchases.map((purchase, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center">
                      <Package className="h-4 w-4 text-blue-500 mr-2" />
                      <span className="font-medium text-gray-900">{purchase.packageId}</span>
                    </div>
                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${purchase.status === 'ACTIVE'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-800'
                      }`}>
                      {purchase.status}
                    </span>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-gray-500">Purchase Date</p>
                      <p className="font-medium">{formatCalendarDate(purchase.purchaseDate)}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Grade</p>
                      <p className="font-medium">{purchase.grade}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Amount</p>
                      <p className="font-medium">${purchase.amount}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Payment Status</p>
                      <p className="font-medium">{purchase.paymentStatus}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Payment Method</p>
                      <p className="font-medium">{purchase.paymentMethod}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Expiry Date</p>
                      <p className="font-medium text-amber-600">
                        {formatCalendarExpiryDate(purchase.purchaseDate)}
                      </p>
                    </div>
                  </div>

                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                      <div className="text-sm text-gray-500 flex-1 min-w-0">
                        <p className="font-medium">Checkout Session:</p>
                        <p className="text-xs break-all leading-relaxed">{purchase.checkoutSessionId}</p>
                      </div>
                      <button
                        onClick={() => downloadReceiptPDF(purchase)}
                        className="flex items-center px-3 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors"
                      >
                        <Download className="h-4 w-4 mr-1" />
                        Download Receipt
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Package className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">No purchase history found</p>
            </div>
          )}
        </div>
      </div>

      {/* Exam Answers Modal */}
      {showAnswersModal && (
        <>
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
              {/* Modal Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <h2 className="text-xl font-semibold text-gray-900">Exam Questions & Answers</h2>
                <button
                  onClick={() => {
                    setShowAnswersModal(false);
                    setExamAnswers(null);
                  }}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="h-5 w-5 text-gray-500" />
                </button>
              </div>

              {/* Modal Content */}
              <div className="flex-1 overflow-y-auto p-6 select-none">
                {examAnswers ? (
                  <div className="space-y-6">
                    {examAnswers.questions && examAnswers.questions.map((question, index) => (
                      <div key={index} className="border border-gray-200 rounded-lg p-4">
                        {/* Question Image - Top - Handle different image field names */}
                        {(question.questionImage || question.image || (question.type === 'multiple-choice' && question.image)) && (
                          <div className="mb-4 text-center">
                            <img
                              src={question.questionImage || question.image}
                              alt={`Question ${question.questionNumber || index + 1}`}
                              className="max-w-md h-48 mx-auto rounded-lg border border-gray-300 shadow-sm"
                            />
                          </div>
                        )}

                        <div className="flex items-start space-x-3">
                          <div className="flex-shrink-0 w-8 h-8 bg-blue-100 text-blue-800 rounded-full flex items-center justify-center font-semibold text-sm">
                            {index + 1}
                          </div>
                          <div className="flex-1">
                            <h3 className="font-medium text-gray-900 mb-2">
                              {question.questionNumber && `Question ${question.questionNumber}: `}
                              <span
                                dangerouslySetInnerHTML={{
                                  __html: question.question || question.text || question.questionText || `Question ${index + 1}`
                                }}
                              />
                            </h3>

                            {/* Options */}
                            {question.options && question.options.length > 0 && (
                              <div className="space-y-2 mb-3">
                                {question.options
                                  .filter(option => {
                                    // Handle different option formats
                                    if (typeof option === 'string') {
                                      return option.trim() !== '';
                                    }
                                    if (typeof option === 'object' && option !== null) {
                                      return (option.text && option.text.trim() !== '') || (option.id && option.id.trim() !== '');
                                    }
                                    return false;
                                  })
                                  .map((option, optIndex) => {
                                    const optionId = typeof option === 'object' ? (option.id || String.fromCharCode(65 + optIndex)) : String.fromCharCode(65 + optIndex);
                                    const optionText = typeof option === 'object' ? (option.text || '') : option;

                                    // Get corresponding image from answerImages array for multiple-choice or MULTI_SELECT
                                    const optionImage = (question.type === 'multiple-choice' || question.type === 'MULTI_SELECT') && question.answerImages && question.answerImages[optIndex]
                                      ? question.answerImages[optIndex]
                                      : null;

                                    return (
                                      <div
                                        key={optIndex}
                                        className={`flex items-start space-x-2 p-2 rounded border select-none ${question.correctAnswer === option.id || question.correctAnswer === optIndex || question.correctAnswer === option
                                            ? 'bg-green-50 border-green-300 text-green-800'
                                            : 'bg-gray-50 border-gray-200 text-gray-700'
                                          }`}
                                      >
                                        <span className="font-medium">
                                          {optionId}:
                                        </span>
                                        <div className="flex-1">
                                          {optionImage ? (
                                            <img
                                              src={optionImage}
                                              alt={`Option ${optionId}`}
                                              className="max-w-full h-auto rounded-lg max-h-20 object-contain"
                                            />
                                          ) : (
                                            <span dangerouslySetInnerHTML={{ __html: optionText }} />
                                          )}
                                        </div>
                                      </div>
                                    );
                                  })}
                              </div>
                            )}

                            {/* Correct Answer */}
                            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                              <p className="text-sm font-medium text-green-800">
                                <span className="font-semibold">Correct Answer:</span>
                                {question.correctAnswer !== undefined ?
                                  (() => {
                                    if (question.type === 'LONG_WRITING' && typeof question.correctAnswer === 'string') {
                                      // Support HTML tags for LONG_WRITING questions
                                      return <span dangerouslySetInnerHTML={{ __html: question.correctAnswer }} />;
                                    } else if (Array.isArray(question.correctAnswer)) {
                                      return question.correctAnswer.map(ans => {
                                        const index = parseInt(ans);
                                        return isNaN(index) ? ans : String.fromCharCode(65 + index);
                                      }).join(', ');
                                    } else if (typeof question.correctAnswer === 'number') {
                                      return String.fromCharCode(65 + question.correctAnswer);
                                    } else if (typeof question.correctAnswer === 'string' && question.correctAnswer.includes(',')) {
                                      // Handle comma-separated indices like "0,4"
                                      return question.correctAnswer.split(',').map(ans => {
                                        const index = parseInt(ans.trim());
                                        return isNaN(index) ? ans.trim() : String.fromCharCode(65 + index);
                                      }).join(', ');
                                    } else {
                                      return question.correctAnswer;
                                    }
                                  })() :
                                  'Not specified'}
                              </p>
                            </div>

                            {/* Explanations for all options - Handle SHORT_ANSWER and other types */}
                            {((question.type === 'SHORT_ANSWER' && question.explanation) ||
                              question.explanations ||
                              question.explanation ||
                              (question.type === 'multiple-choice' && question.explanations) ||
                              (question.type === 'MULTI_SELECT' && question.explanations)) && (
                                <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                                  <p className="text-sm font-medium text-gray-700 mb-2">Explanation:</p>
                                  {(() => {
                                    // For SHORT_ANSWER and LONG_WRITING, prioritize explanation field, otherwise use explanations
                                    const explanationsData = (question.type === 'SHORT_ANSWER' || question.type === 'LONG_WRITING')
                                      ? question.explanation || question.explanations
                                      : question.explanations || question.explanation;

                                    // Handle SHORT_ANSWER and LONG_WRITING types where explanation is a string
                                    if ((question.type === 'SHORT_ANSWER' || question.type === 'LONG_WRITING') && typeof explanationsData === 'string') {
                                      return (
                                        <p className="text-sm text-gray-700 leading-relaxed" dangerouslySetInnerHTML={{ __html: explanationsData }} />
                                      );
                                    } else if (typeof explanationsData === 'object' && !Array.isArray(explanationsData)) {
                                      return (
                                        <div className="space-y-1">
                                          {Object.entries(explanationsData).map(([key, explanation]) => {
                                            // Convert correctAnswer indices to letters for comparison
                                            let correctAnswersAsLetters = [];
                                            if (Array.isArray(question.correctAnswer)) {
                                              correctAnswersAsLetters = question.correctAnswer.map(ans => {
                                                const index = parseInt(ans);
                                                return isNaN(index) ? ans : String.fromCharCode(65 + index);
                                              });
                                            } else if (typeof question.correctAnswer === 'string' && question.correctAnswer.includes(',')) {
                                              // Handle comma-separated indices like "0,4"
                                              correctAnswersAsLetters = question.correctAnswer.split(',').map(ans => {
                                                const index = parseInt(ans.trim());
                                                return isNaN(index) ? ans.trim() : String.fromCharCode(65 + index);
                                              });
                                            } else if (typeof question.correctAnswer === 'number') {
                                              correctAnswersAsLetters = [String.fromCharCode(65 + question.correctAnswer)];
                                            } else {
                                              correctAnswersAsLetters = [question.correctAnswer];
                                            }
                                            const isCorrectOption = correctAnswersAsLetters.includes(key);

                                            return (
                                              <div key={key} className={`text-sm leading-relaxed ${isCorrectOption ? 'text-green-700' : 'text-red-700'}`}>
                                                <span className={`font-medium ${isCorrectOption ? 'text-green-800' : 'text-red-800'}`}>
                                                  {key}:
                                                </span> <span dangerouslySetInnerHTML={{ __html: explanation }} />
                                              </div>
                                            );
                                          })}
                                        </div>
                                      );
                                    } else {
                                      return (
                                        <p className="text-sm text-gray-700 leading-relaxed" dangerouslySetInnerHTML={{ __html: explanationsData }} />
                                      );
                                    }
                                  })()}
                                </div>
                              )}

                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-500">Loading exam answers...</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {/* My Answers Modal */}
      {showMyAnswersModal && (
        <>
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
              {/* Modal Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <h2 className="text-xl font-semibold text-gray-900">My Answers</h2>
                <button
                  onClick={() => {
                    setShowMyAnswersModal(false);
                    setSelectedExamAnswers(null);
                  }}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="h-5 w-5 text-gray-500" />
                </button>
              </div>

              {/* Modal Content */}
              <div className="flex-1 overflow-y-auto p-6">
                {selectedExamAnswers && selectedExamAnswers.answers ? (
                  <div className="space-y-4">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                      <h3 className="font-medium text-blue-900 mb-2">Exam Details</h3>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="font-medium text-blue-700">Grade:</span> {selectedExamAnswers.grade}
                        </div>
                        <div>
                          <span className="font-medium text-blue-700">Package:</span> {selectedExamAnswers.package}
                        </div>
                        <div>
                          <span className="font-medium text-blue-700">Type:</span> {selectedExamAnswers.type}
                        </div>
                        <div>
                          <span className="font-medium text-blue-700">Set:</span> {selectedExamAnswers.set}
                        </div>
                        <div>
                          <span className="font-medium text-blue-700">Score:</span> {selectedExamAnswers.score}/{selectedExamAnswers.answers.length}
                        </div>
                        <div>
                          <span className="font-medium text-blue-700">Percentage:</span> {selectedExamAnswers.percentage}%
                        </div>
                      </div>
                    </div>

                    <h3 className="font-medium text-gray-900 mb-4">Your Answers</h3>
                    <div className="space-y-3">
                      {selectedExamAnswers.answers.map((answer, index) => (
                        <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                          <div className="flex items-center space-x-3">
                            <div className="flex-shrink-0 w-8 h-8 bg-gray-200 text-gray-700 rounded-full flex items-center justify-center font-semibold text-sm">
                              {answer.questionNumber}
                            </div>
                            <div>
                              <span className="text-sm font-medium text-gray-900">Question {answer.questionNumber}</span>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            {answer.userAnswer === 'did not answer' ? (
                              <span className="px-3 py-1 bg-red-100 text-red-700 text-sm rounded-full font-medium">
                                Not Answered
                              </span>
                            ) : (
                              <span className="px-3 py-1 bg-green-100 text-green-700 text-sm rounded-full font-medium">
                                {Array.isArray(answer.userAnswer) ? (
                                  answer.userAnswer.join(', ')
                                ) : (
                                  <span dangerouslySetInnerHTML={{ __html: answer.userAnswer }} />
                                )}
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-500">Loading your answers...</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default UserDetails;